import { z } from "zod";
import { IdPageQuery, IsoDateTime, pageOf, Uuid } from "./common";

export const AI_EFFORT_VALUES = ["low", "medium", "high", "xhigh", "max"] as const;
export const AI_IMAGE_PROVIDERS = ["gemini", "openai"] as const;
export const AI_PAYER_VALUES = ["service", "host", "tenant"] as const;

export const AiSettingsDto = z.object({
  generatorModel: z.string(),
  judgeModel: z.string(),
  visionModel: z.string(),
  imageProvider: z.enum(AI_IMAGE_PROVIDERS),
  effort: z.enum(AI_EFFORT_VALUES),
  monthlyCapUsd: z.number(),
  alertThresholdPct: z.number().int(),
  /** The models this service can price, for the settings form. */
  availableModels: z.array(z.string()),
});
export type AiSettingsDto = z.infer<typeof AiSettingsDto>;

export const AiSettingsPut = z
  .object({
    generatorModel: z.string().min(1).max(100),
    judgeModel: z.string().min(1).max(100),
    visionModel: z.string().min(1).max(100),
    imageProvider: z.enum(AI_IMAGE_PROVIDERS),
    effort: z.enum(AI_EFFORT_VALUES),
    monthlyCapUsd: z.number().min(0).max(100_000),
    alertThresholdPct: z.number().int().min(1).max(100),
  })
  .partial()
  .strict();
export type AiSettingsPut = z.infer<typeof AiSettingsPut>;

export const AiUsageQuery = z.object({
  /** YYYY-MM in the tenant's timezone. Default: the current month. */
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/u)
    .optional(),
  purpose: z.string().min(1).max(100).optional(),
});
export type AiUsageQuery = z.infer<typeof AiUsageQuery>;

/** One line per purpose, provider, model and payer in a month. A host imports these into its own ledger. */
export const AiUsageItem = z.object({
  /** Stable across exports of the same month: upsert on it, and a re-import replaces the line with the newer totals. */
  externalKey: z.string(),
  month: z.string(),
  purpose: z.string(),
  provider: z.string(),
  model: z.string(),
  payer: z.enum(AI_PAYER_VALUES),
  /** Calls that returned a usable answer. */
  runs: z.number().int(),
  /** Calls that failed or were refused by the model. They can still have a cost. */
  failedRuns: z.number().int(),
  /** Calls stopped by the monthly cap, which reached no provider. */
  blockedRuns: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  imageCount: z.number().int(),
  costMicroUsd: z.number().int(),
});
export type AiUsageItem = z.infer<typeof AiUsageItem>;

export const AiUsageResponse = z.object({
  tenantId: z.string(),
  month: z.string(),
  currency: z.literal("USD"),
  capUsd: z.number(),
  /** Whole month, whatever `purpose` filtered the items to. */
  spentMicroUsd: z.number().int(),
  asOf: IsoDateTime,
  items: z.array(AiUsageItem),
});
export type AiUsageResponse = z.infer<typeof AiUsageResponse>;

/** What a screen asks for: the month, grouped by the tenant's own days. */
export const AiUsageDaysQuery = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/u)
    .optional(),
});
export type AiUsageDaysQuery = z.infer<typeof AiUsageDaysQuery>;

/** One day, purpose and model. Only counts and money: a prompt or an answer is never in the ledger's reports. */
export const AiUsageDayRow = z.object({
  /** YYYY-MM-DD in the tenant's timezone. */
  day: z.string(),
  purpose: z.string(),
  provider: z.string(),
  model: z.string(),
  calls: z.number().int(),
  /** Of `calls`, those that failed, were refused by the model, or were stopped by the cap. */
  notOk: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  imageCount: z.number().int(),
  costMicroUsd: z.number().int(),
});
export type AiUsageDayRow = z.infer<typeof AiUsageDayRow>;

export const AiUsageDaysResponse = z.object({
  tenantId: z.string(),
  month: z.string(),
  currency: z.literal("USD"),
  capUsd: z.number(),
  /** The percentage of the cap at which the tenant gets its alert. */
  alertThresholdPct: z.number().int(),
  spentMicroUsd: z.number().int(),
  payer: z.enum(AI_PAYER_VALUES),
  asOf: IsoDateTime,
  rows: z.array(AiUsageDayRow),
});
export type AiUsageDaysResponse = z.infer<typeof AiUsageDaysResponse>;

export const AiUsageCallQuery = IdPageQuery.extend({
  day: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u)
    .optional(),
  purpose: z.string().min(1).max(100).optional(),
  model: z.string().min(1).max(100).optional(),
});
export type AiUsageCallQuery = z.infer<typeof AiUsageCallQuery>;

export const AI_RUN_STATUSES = ["ok", "failed", "refused", "cap_reached"] as const;

/** One call. What it was for and what it used; what was said in it is not here, and is not kept. */
export const AiUsageCall = z.object({
  id: Uuid,
  at: IsoDateTime,
  purpose: z.string(),
  provider: z.string(),
  model: z.string(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  imageCount: z.number().int(),
  costMicroUsd: z.number().int(),
  status: z.enum(AI_RUN_STATUSES),
});
export type AiUsageCall = z.infer<typeof AiUsageCall>;

export const AiUsageCallPage = pageOf(AiUsageCall);
export type AiUsageCallPage = z.infer<typeof AiUsageCallPage>;

export const HOST_AI_USAGE_MAX_LIMIT = 500;

export const HostAiUsageQuery = z.object({
  /** The `cursor` of the previous answer. Without it the export starts at the first call there is. */
  cursor: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(HOST_AI_USAGE_MAX_LIMIT).default(200),
  /** One tenant, by Imagora id or `ref:<hostRef>`. Without it, all the host's tenants. */
  tenant: z.string().min(1).max(200).optional(),
});
export type HostAiUsageQuery = z.infer<typeof HostAiUsageQuery>;

/** One call in a host's ledger. `id` never changes and a row is never edited, so a repeated pull upserts on it and changes nothing. */
export const HostAiUsageItem = AiUsageCall.extend({
  tenantId: Uuid,
  /** The host's own id for the tenant. */
  tenantRef: z.string(),
  /** YYYY-MM in the tenant's timezone: the month the cap and the host's invoice count this call in. */
  month: z.string(),
  effort: z.string().nullable(),
  /** `host` is what the host is invoiced for, when `status` is `ok`. The rest is there so its figures agree with the tenant's screen. */
  payer: z.enum(AI_PAYER_VALUES),
});
export type HostAiUsageItem = z.infer<typeof HostAiUsageItem>;

export const HostAiUsagePage = z.object({
  currency: z.literal("USD"),
  items: z.array(HostAiUsageItem),
  /** Send it back next time. It stays what you sent when nothing new has settled, so a poll can always keep the last one. */
  cursor: Uuid.nullable(),
  /** True when the next call would return more right away. */
  hasMore: z.boolean(),
  /** Calls newer than this are held back a moment, because one still being written could otherwise land behind a cursor and be missed. */
  settledUntil: IsoDateTime,
});
export type HostAiUsagePage = z.infer<typeof HostAiUsagePage>;
